import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { PageHeader } from '@/components/layout/PageHeader';
import { QueryBoundary } from '@/components/query-boundary';
import { SectionCard } from '@/components/section-card';
import { Button } from '@/components/ui/button';
import { useMemoryVisitors } from '@/lib/queries/memory';
import { normalizeMessagesPage } from '@/lib/route-search';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const Route = createFileRoute('/memory/')({
  validateSearch: (search: Record<string, unknown>) => ({ page: normalizeMessagesPage(search.page) }),
  component: MemoryListPage,
});

const LIMIT = 20;

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
}

function MemoryListPage() {
  const { page } = Route.useSearch();
  const navigate = useNavigate({ from: '/memory/' });
  const q = useMemoryVisitors(page, LIMIT);

  function changePage(next: number) {
    void navigate({ search: { page: next } });
  }

  return (
    <>
      <PageHeader title="기억" description="방문자 기억 (temporal KG)" />
      <QueryBoundary query={q}>
        {data => {
          const totalPages = Math.max(1, Math.ceil(data.total / data.limit));
          return (
            <SectionCard title="방문자" count={data.total} noPadding>
              {data.data.length === 0 ? (
                <p className="py-10 text-center text-[13px] text-muted-foreground">기억된 방문자가 없습니다.</p>
              ) : (
                <ul className="divide-y divide-border/70">
                  {data.data.map(row => (
                    <li key={row.visitor_id}>
                      <Link
                        to="/memory/$visitorId"
                        params={{ visitorId: row.visitor_id }}
                        className="grid grid-cols-3 items-start gap-x-3 gap-y-2 px-4 py-3 transition-colors hover:bg-accent/45 lg:flex lg:items-center lg:gap-4 lg:px-5"
                      >
                        <span className="col-span-3 min-w-0 break-all font-mono text-[12px] text-muted-foreground lg:col-span-1 lg:w-40 lg:shrink-0 lg:truncate">
                          {row.visitor_id}
                        </span>
                        <span className="min-w-0 break-words font-mono text-[13px] tabular-nums lg:w-20 lg:shrink-0 lg:whitespace-nowrap">사실 {row.valid_facts}</span>
                        <span className="min-w-0 break-words font-mono text-[13px] tabular-nums lg:w-20 lg:shrink-0 lg:whitespace-nowrap">개체 {row.entities}</span>
                        <span className="min-w-0 break-words font-mono text-[13px] tabular-nums lg:w-20 lg:shrink-0 lg:whitespace-nowrap">세션 {row.session_count}</span>
                        <span className="col-span-3 min-w-0 break-words text-[12.5px] text-muted-foreground lg:col-span-1 lg:flex-1 lg:truncate">
                          {row.first_message ?? '—'}
                        </span>
                        <span className="col-span-3 text-[12px] text-muted-foreground lg:col-span-1 lg:shrink-0">
                          {formatDate(row.last_activity_at)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex items-center justify-end gap-2 border-t border-border/70 px-4 py-2.5">
                <span className="text-[12px] text-muted-foreground">{page} / {totalPages}</span>
                <Button aria-label="이전 페이지" size="sm" variant="outline" disabled={page <= 1} onClick={() => changePage(page - 1)}>
                  <ChevronLeft />
                </Button>
                <Button aria-label="다음 페이지" size="sm" variant="outline" disabled={page >= totalPages} onClick={() => changePage(page + 1)}>
                  <ChevronRight />
                </Button>
              </div>
            </SectionCard>
          );
        }}
      </QueryBoundary>
    </>
  );
}
